/*
 * Copyright (c) 2024-2025. Haodong JU
 */

import { VSelect, VTextarea, VTextField } from 'vuetify/lib/components'
import EnhancedColorPicker from '@/model/baseForm/fields/EnhancedColorPicker.vue'
import EnhancedImageUploader from '@/model/baseForm/fields/EnhancedImageUploader.vue'
import Asset from '@/model/dataLayer/api/MembershipSystem/Asset'
import { timeArrays } from '@/model/dataLayer/api/common/OpenTimeList'
import { AllMonthDisplay } from '@/model/utils/utils'
import {
  createAssetSelectorSchema,
  createEntitySelectorSchema,
  createProductSelectorSchema
} from '@/model/baseForm/fields/EntitySelector'

/**
 * Creates form fields for display information (name, description, color, image)
 *
 * @param {Object} options - Configuration options
 * @param {string} options.keyPrefix - Prefix for the field keys (default: 'displayInfo')
 * @param {string} options.nameKey - Key for the name field (default: 'name')
 * @param {string} options.descriptionKey - Key for the description field (default: 'description')
 * @param {string} options.colorKey - Key for the color field (default: 'color')
 * @param {string} options.imageKey - Key for the image field (default: 'imageUrl')
 * @param {boolean} options.useNestedKeys - Whether to use nested keys with keyPrefix (default: true)
 * @param {boolean} options.nameRequired - Whether the name field is required (default: true)
 * @param {boolean} options.descriptionRequired - Whether the description field is required (default: false)
 * @param {boolean} options.colorRequired - Whether the color field is required (default: false)
 * @param {boolean} options.imageRequired - Whether the image field is required (default: false)
 * @param {string} options.nameHint - Hint text for the name field (default: '权益名称')
 * @param {string} options.descriptionHint - Hint text for the description field (default: '权益描述')
 * @param {string} options.colorHint - Hint text for the color field (default: '权益颜色')
 * @param {string} options.imageHint - Hint text for the image field (default: '权益图片')
 * @param {Object} options.componentProps - Custom props for each component (name, description, color, imageUrl)
 * @param {Object} options.preconditions - Preconditions for showing the fields
 * @returns {Array} Array of form field objects
 */
export function createDisplayInfoFormFields (options = {}) {
  const {
    keyPrefix = 'displayInfo',
    nameKey = 'name',
    descriptionKey = 'description',
    colorKey = 'color',
    imageKey = 'imageUrl',
    useNestedKeys = true,
    nameRequired = true,
    descriptionRequired = false,
    colorRequired = false,
    imageRequired = false,
    nameHint = '权益名称',
    descriptionHint = '权益描述',
    colorHint = '权益颜色',
    imageHint = '权益图片',
    componentProps = {},
    preconditions = null
  } = options

  // Helper function to generate the key based on whether nested keys are used
  const getKey = (key) => useNestedKeys ? `${keyPrefix}.${key}` : key

  return [
    {
      name: '名称',
      key: getKey(nameKey),
      component: VTextField,
      required: nameRequired,
      hint: nameHint,
      preconditions,
      componentProps: componentProps[nameKey] || {}
    },
    {
      name: '描述',
      key: getKey(descriptionKey),
      component: VTextarea,
      required: descriptionRequired,
      hint: descriptionHint,
      preconditions,
      componentProps: componentProps[descriptionKey] || {}
    },
    {
      name: '颜色',
      key: getKey(colorKey),
      component: EnhancedColorPicker,
      required: colorRequired,
      hint: colorHint,
      default: '',
      preconditions,
      componentProps: componentProps[colorKey] || {}
    },
    {
      name: '图片',
      key: getKey(imageKey),
      component: EnhancedImageUploader,
      required: imageRequired,
      hint: imageHint,
      default: '',
      preconditions,
      componentProps: componentProps[imageKey] || {}
    }
  ]
}

/**
 * Returns the form schema for the membership benefit form
 *
 * @returns {Array} The form schema
 */
export function getMembershipBenefitFormSchema () {
  return [
    ...createDisplayInfoFormFields(),
    {
      name: '权益类型',
      key: 'benefitType',
      component: VSelect,
      required: true,
      hint: '权益类型',
      onChanged: (model) => {
        console.log('benefitType changed', model)
        if (model.benefitType === 'DESCRIPTIVE' && model.trigger) {
          // Reset trigger if changing from RULE_BASED to DESCRIPTIVE
          model.trigger = {
            triggerType: '',
            assetId: null,
            maxTriggersPerUser: 1,
            triggerConfig: '{}'
          }
        } else if (model.benefitType === 'RULE_BASED' && (!model.trigger || !model.trigger.triggerType)) {
          // Initialize trigger if changing from DESCRIPTIVE to RULE_BASED
          model.trigger = {
            triggerType: '',
            assetId: null,
            maxTriggersPerUser: 1,
            triggerConfig: '{}'
          }
        }
      },
      componentProps: {
        items: [
          { text: 'RULE_BASED', value: 'RULE_BASED' },
          { text: 'DESCRIPTIVE', value: 'DESCRIPTIVE' }
        ],
        itemText: 'text',
        itemValue: 'value'
      }
    },
    createEntitySelectorSchema({
      key: 'requiredLevelOrder',
      name: '会员等级',
      hint: '选择会员等级',
      required: false,
      selectionOnly: true,
      itemValue: 'levelOrder',
      loadFunction: async () => {
        const MembershipLevel = (await import('@/model/dataLayer/api/MembershipSystem/MembershipLevel')).default
        return await MembershipLevel.getList(true)
      },
      addFunction: async () => {
      }, // Not used in selection-only mode
      formSchemas: [] // Not used in selection-only mode
    }),
    createAssetSelectorSchema({
      key: 'trigger.assetId',
      name: '奖励',
      hint: '选择奖励资产',
      required: true,
      selectionOnly: false,
      loadFunction: async () => await Asset.getList(true),
      saveFunction: async (newAsset) => {
        return await Asset.add(newAsset)
      },
      preconditions: {
        benefitType: 'RULE_BASED'
      }
    }),
    {
      name: '触发器类型',
      key: 'trigger.triggerType',
      component: VSelect,
      required: true,
      hint: '触发器类型',
      preconditions: {
        benefitType: 'RULE_BASED'
      },
      componentProps: {
        items: [
          { text: 'WEEKLY', value: 'WEEKLY' },
          { text: 'MONTHLY', value: 'MONTHLY' },
          { text: 'YEARLY', value: 'YEARLY' },
          { text: 'BIRTHDAY_MONTH', value: 'BIRTHDAY_MONTH' },
          { text: 'WEEKLY_TIME_RANGE_WITH_ACTION', value: 'WEEKLY_TIME_RANGE_WITH_ACTION' },
          { text: 'ORDER_COMPLETED', value: 'ORDER_COMPLETED' },
          { text: 'LEVEL_REACHED', value: 'LEVEL_REACHED' }
        ],
        itemText: 'text',
        itemValue: 'value'
      }
    },

    {
      name: '每个人最大获得数量',
      key: 'trigger.maxTriggersPerUser',
      component: VTextField,
      required: false,
      hint: '每个用户最多可以获得的次数',
      preconditions: {
        benefitType: 'RULE_BASED'
      },
      componentProps: {
        type: 'number'
      }
    },
    // WEEKLY trigger fields
    {
      name: '时间',
      key: 'weeklyConfig.timeOfDay',
      component: VSelect,
      required: true,
      hint: '触发时间',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'WEEKLY'
      },
      componentProps: {
        items: timeArrays,
        itemText: 'start',
        itemValue: 'start'
      }
    },
    {
      name: '星期几',
      key: 'weeklyConfig.dayOfWeek',
      component: VSelect,
      required: true,
      hint: '触发的星期几',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'WEEKLY'
      },
      componentProps: {
        items: [
          { text: '星期一', value: 1 },
          { text: '星期二', value: 2 },
          { text: '星期三', value: 3 },
          { text: '星期四', value: 4 },
          { text: '星期五', value: 5 },
          { text: '星期六', value: 6 },
          { text: '星期日', value: 7 }
        ],
        itemText: 'text',
        itemValue: 'value'
      }
    },
    // MONTHLY trigger fields
    {
      name: '时间',
      key: 'monthlyConfig.timeOfDay',
      component: VSelect,
      required: true,
      hint: '触发时间',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'MONTHLY'
      },
      componentProps: {
        items: timeArrays,
        itemText: 'start',
        itemValue: 'start'
      }
    },
    {
      name: '日期',
      key: 'monthlyConfig.dayOfMonth',
      component: VSelect,
      required: true,
      hint: '触发的月份日期',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'MONTHLY'
      },
      componentProps: {
        items: Array.from({ length: 31 }, (_, i) => ({ text: `${i + 1}日`, value: i + 1 })),
        itemText: 'text',
        itemValue: 'value'
      }
    },
    // YEARLY trigger fields
    {
      name: '时间',
      key: 'yearlyConfig.timeOfDay',
      component: VSelect,
      required: true,
      hint: '触发时间',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'YEARLY'
      },
      componentProps: {
        items: timeArrays,
        itemText: 'start',
        itemValue: 'start'
      }
    },
    {
      name: '月份',
      key: 'yearlyConfig.month',
      component: VSelect,
      required: true,
      hint: '触发的月份',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'YEARLY'
      },
      componentProps: {
        items: AllMonthDisplay,
        itemText: 'name',
        itemValue: 'value'
      }
    },
    {
      name: '日期',
      key: 'yearlyConfig.day',
      component: VSelect,
      required: true,
      hint: '触发的日期',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'YEARLY',
        'yearlyConfig.month': (value) => !!value
      },
      componentProps: {
        items: Array.from({ length: 31 }, (_, i) => ({ text: `${i + 1}日`, value: i + 1 })),
        itemText: 'text',
        itemValue: 'value'
      }
    },
    // BIRTHDAY_MONTH trigger fields
    {
      name: '时间',
      key: 'birthdayMonthConfig.timeOfDay',
      component: VSelect,
      required: true,
      hint: '触发时间',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'BIRTHDAY_MONTH'
      },
      componentProps: {
        items: timeArrays,
        itemText: 'start',
        itemValue: 'start'
      }
    },
    // WEEKLY_TIME_RANGE_WITH_ACTION trigger fields
    {
      name: '开始时间',
      key: 'weeklyTimeRangeConfig.startTime',
      component: VSelect,
      required: true,
      hint: '开始时间',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'WEEKLY_TIME_RANGE_WITH_ACTION'
      },
      componentProps: {
        items: timeArrays,
        itemText: 'start',
        itemValue: 'start'
      }
    },
    {
      name: '结束时间',
      key: 'weeklyTimeRangeConfig.endTime',
      component: VSelect,
      required: true,
      hint: '结束时间',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'WEEKLY_TIME_RANGE_WITH_ACTION'
      },
      componentProps: {
        items: timeArrays,
        itemText: 'start',
        itemValue: 'start'
      }
    },
    {
      name: '星期几',
      key: 'weeklyTimeRangeConfig.dayOfWeek',
      component: VSelect,
      required: true,
      hint: '触发的星期几',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'WEEKLY_TIME_RANGE_WITH_ACTION'
      },
      componentProps: {
        items: [
          { text: '星期一', value: 1 },
          { text: '星期二', value: 2 },
          { text: '星期三', value: 3 },
          { text: '星期四', value: 4 },
          { text: '星期五', value: 5 },
          { text: '星期六', value: 6 },
          { text: '星期日', value: 7 }
        ],
        itemText: 'text',
        itemValue: 'value'
      }
    },
    // ORDER_COMPLETED trigger fields
    {
      name: '最低消费金额',
      key: 'orderTriggerConfig.minimumSpend',
      component: VTextField,
      required: true,
      hint: '触发所需的最低消费金额',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'ORDER_COMPLETED'
      },
      componentProps: {
        type: 'number'
      }
    },
    createProductSelectorSchema({
      key: 'orderTriggerConfig.specificProducts',
      name: '特定产品',
      hint: '触发所需的特定产品',
      required: false,
      multiple: true,
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'ORDER_COMPLETED'
      }
    }),
    {
      name: '冷却天数',
      key: 'orderTriggerConfig.cooldownDays',
      component: VTextField,
      required: true,
      hint: '防止频繁触发的冷却天数',
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'ORDER_COMPLETED'
      },
      componentProps: {
        type: 'number'
      }
    },
    // LEVEL_REACHED trigger fields
    createEntitySelectorSchema({
      key: 'levelTriggerConfig.targetLevelId',
      name: '目标等级',
      hint: '触发所需达到的会员等级',
      required: true,
      selectionOnly: true,
      loadFunction: async () => {
        const MembershipLevel = (await import('@/model/dataLayer/api/MembershipSystem/MembershipLevel')).default
        return await MembershipLevel.getList(true)
      },
      addFunction: async () => {
      }, // Not used in selection-only mode
      formSchemas: [], // Not used in selection-only mode
      preconditions: {
        benefitType: 'RULE_BASED',
        'trigger.triggerType': 'LEVEL_REACHED'
      }
    })
  ]
}
